import { useSuspenseQueries } from '@tanstack/react-query';

import type * as api from '@gitbook/api-client';
import type * as analytics from '@gitbook/app-analytics';
import type { NavigationScreenSpaceState } from '@gitbook/app-navigation';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    EmptyState,
    EmptyStateActions,
    EmptyStateIcon,
    Heading,
} from '@gitbook/spine-react';

import { SitePreviewScreen, useSpacePrimarySite } from '../../../Components';
import { useSetAnalyticsScreen } from '../../../navigation';
import { apiQuery } from '../../../state';
import { SpaceHeadTitle } from '../Screen/SpaceHeadTitle';
import { useSavedRevision } from './useSavedRevision';

/**
 * Preview for a space.
 */
export function SpacePreview(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
    screen: NavigationScreenSpaceState;
    analyticsContext: analytics.AnalyticsContextSpace;
}) {
    const { space, changeRequest, screen, analyticsContext } = props;

    const [{ data: organization }, { data: organizationContent }] = useSuspenseQueries({
        queries: [
            apiQuery('getOrganizationById', {
                organizationId: space.organization,
            }),
            apiQuery('getOrganizationAllContent', {
                organizationId: space.organization,
            }),
        ],
    });
    const siteLookup = useSpacePrimarySite(organizationContent, space.id);
    const revisionId = useSavedRevision(
        screen.revision ?? changeRequest?.revision ?? space.revision
    );

    useSetAnalyticsScreen({
        ...analyticsContext,
        screen: 'space_preview',
    });

    return (
        <>
            <SpaceHeadTitle title="Preview" space={space} />
            {!siteLookup ? (
                <SpacePreviewBlankslate />
            ) : (
                <SitePreviewScreen
                    organization={organization}
                    site={siteLookup.site}
                    loading={!revisionId}
                    content={{
                        siteSpaceId: siteLookup.siteSpace.id,
                        revisionId,
                        pagePath: screen.page,
                    }}
                    canonicalContent={{
                        siteSpaceId: siteLookup.siteSpace.id,
                        changeRequestNumber: changeRequest?.number,
                        revisionId: screen.revision,
                        pagePath: screen.page,
                    }}
                />
            )}
        </>
    );
}

function SpacePreviewBlankslate() {
    return (
        <div className="flex flex-1 items-center justify-center bg-muted">
            <EmptyState>
                <EmptyStateIcon>
                    <Icon.DocsSite />
                </EmptyStateIcon>
                <Heading>Publish as a docs site</Heading>
                <Description>
                    This space cannot be previewed before being part of a docs site. Publish it as a
                    docs site to share your content with the world.
                </Description>
                <EmptyStateActions>
                    <Button
                        href={{ screen: { sideSheet: { type: 'share', tab: 'docs-sites' } } }}
                        label="Publish as a docs site"
                        kind="secondary"
                    />
                </EmptyStateActions>
            </EmptyState>
        </div>
    );
}
